<template>
  <el-popover
    v-model:visible="toastPopoverOpen"
    placement="bottom-end"
    :width="340"
    trigger="manual"
    transition="header-bell-toast-transition"
    :show-arrow="true"
    :teleported="true"
    :z-index="4600"
    :popper-class="['header-bell-toast-popper', { 'bell-toast-fading': toastFading }]"
  >
    <template #reference>
      <button
        :class="['bell-trigger', { 'bell-pulse': bellPulse }]"
        type="button"
        aria-label="消息通知"
        @click.stop="onBellClick"
      >
        <span class="bell-inner">
          <el-icon class="bell-icon"><Bell /></el-icon>
          <el-badge
            v-if="unreadCount > 0"
            :value="unreadCount"
            :max="99"
            class="bell-badge-floating"
          />
        </span>
      </button>
    </template>
    <div
      class="bell-toast-panel"
      @mouseenter="onToastPanelEnter"
      @mouseleave="onToastPanelLeave"
    >
      <div v-if="!pendingToasts.length" class="bell-toast-empty">暂无新提醒</div>
      <ul v-else class="bell-toast-list">
        <li v-for="t in visibleToasts" :key="t.id" class="bell-toast-item">
          <div class="bell-toast-text">
            <div class="bell-toast-title">{{ toastLine(t) }}</div>
            <div v-if="t.content" class="bell-toast-sub">{{ t.content }}</div>
          </div>
          <div class="bell-toast-actions">
            <el-button type="primary" link class="bell-toast-link" @click="openToast(t)">
              {{ t.actionLabel || '点击查看' }}
            </el-button>
            <el-button
              v-if="t.secondaryActionLabel && toastSecondaryActionId(t)"
              type="primary"
              link
              class="bell-toast-link bell-toast-link-secondary"
              @click="runToastSecondaryAction(t)"
            >
              {{ t.secondaryActionLabel }}
            </el-button>
          </div>
        </li>
      </ul>
      <div class="bell-toast-footer">
        <el-button text type="primary" size="small" @click="goNotificationCenter">进入消息中心</el-button>
      </div>
    </div>
  </el-popover>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Bell } from '@element-plus/icons-vue';
import {
  getPendingToasts,
  markNotificationRead,
  type NotificationItem,
} from '@/api/notificationApi';
import { rerouteBellToastToTabDot } from '@/utils/pageTabCompletionNotify';
import { copyXindiShareToArchive } from '@/api/xindiLwApi';
import { saveCustomFormulaShareToMine, formatApiErrorMessage } from '@/api/calculatorFormulaShareApi';
import { appendArchivedShareJobToLocalStorage } from '@/utils/xindiViewerLocalJobs';
import { resolveNotificationNavigation } from '@/utils/notificationNavigation';
import { ElMessage } from 'element-plus';

const props = defineProps<{
  unreadCount: number;
}>();

const emit = defineEmits<{
  (e: 'refresh-unread'): void;
}>();

const router = useRouter();
const toastPopoverOpen = ref(false);
const pendingToasts = ref<NotificationItem[]>([]);
const bellPulse = ref(false);
const lastToastSignature = ref('');
const toastFading = ref(false);
const autoDismissActive = ref(false);

/** 铃铛弹框默认最多展示条数 */
const TOAST_DISPLAY_LIMIT = 2;
/** 新通知推送：3s 后开始淡化，5s 后自动关闭 */
const TOAST_FADE_START_MS = 3000;
const TOAST_AUTO_CLOSE_MS = 5000;

const visibleToasts = computed(() => pendingToasts.value.slice(0, TOAST_DISPLAY_LIMIT));

let pollTimer: ReturnType<typeof setInterval> | null = null;
let fadeTimer: ReturnType<typeof setTimeout> | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;
let dismissPaused = false;
let dismissElapsedMs = 0;
let dismissResumeAt = 0;

/** 拉取待展示铃铛列表，间隔不宜过短以免干扰排障与增加服务端压力 */
const POLL_MS = 60000;
let toastPollPrimed = false;

function clearAutoDismissTimers() {
  if (fadeTimer != null) {
    window.clearTimeout(fadeTimer);
    fadeTimer = null;
  }
  if (closeTimer != null) {
    window.clearTimeout(closeTimer);
    closeTimer = null;
  }
}

function stopAutoDismiss() {
  autoDismissActive.value = false;
  dismissPaused = false;
  dismissElapsedMs = 0;
  toastFading.value = false;
  clearAutoDismissTimers();
}

function closeAutoDismissPopover() {
  toastFading.value = false;
  toastPopoverOpen.value = false;
  stopAutoDismiss();
}

function scheduleAutoDismissFrom(elapsedMs: number) {
  clearAutoDismissTimers();
  toastFading.value = false;

  const fadeDelay = Math.max(0, TOAST_FADE_START_MS - elapsedMs);
  const closeDelay = Math.max(0, TOAST_AUTO_CLOSE_MS - elapsedMs);

  if (closeDelay <= 0) {
    closeAutoDismissPopover();
    return;
  }

  fadeTimer = window.setTimeout(() => {
    if (!dismissPaused && autoDismissActive.value) {
      toastFading.value = true;
    }
  }, fadeDelay);

  closeTimer = window.setTimeout(() => {
    if (!dismissPaused && autoDismissActive.value) {
      closeAutoDismissPopover();
    }
  }, closeDelay);
}

function startAutoDismiss() {
  autoDismissActive.value = true;
  dismissPaused = false;
  dismissElapsedMs = 0;
  dismissResumeAt = Date.now();
  scheduleAutoDismissFrom(0);
}

function onToastPanelEnter() {
  if (!autoDismissActive.value || !toastPopoverOpen.value || dismissPaused) return;
  dismissPaused = true;
  dismissElapsedMs += Date.now() - dismissResumeAt;
  clearAutoDismissTimers();
  toastFading.value = false;
}

function onToastPanelLeave() {
  if (!autoDismissActive.value || !toastPopoverOpen.value || !dismissPaused) return;
  dismissPaused = false;
  dismissResumeAt = Date.now();
  scheduleAutoDismissFrom(dismissElapsedMs);
}

function toastLine(t: NotificationItem) {
  const title = (t.title || '').trim();
  if (!title) return '新通知';
  if (t.type === 'ISSUE_URGE') return title;
  if (title.includes('已完成') || title.includes('点击')) return title;
  if (title.includes('分享')) return title;
  return `${title}已完成，点击查看`;
}

function archiveTitleFromNotification(t: NotificationItem): string {
  const c = (t.content || '').trim();
  const m = c.match(/资源：\s*([^\n]+)/);
  if (m && m[1]) return m[1].trim();
  return '分享的离线预览';
}

function xindiSharePublicId(t: NotificationItem): string | null {
  const raw = (t.extraJson || '').trim();
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as { xindiSharePublicId?: string };
    const id = typeof o?.xindiSharePublicId === 'string' ? o.xindiSharePublicId.trim() : '';
    return id || null;
  } catch {
    return null;
  }
}

function formulaSharePublicId(t: NotificationItem): string | null {
  const raw = (t.extraJson || '').trim();
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as { formulaSharePublicId?: string };
    const id = typeof o?.formulaSharePublicId === 'string' ? o.formulaSharePublicId.trim() : '';
    return id || null;
  } catch {
    return null;
  }
}

function toastSecondaryActionId(t: NotificationItem): string | null {
  return xindiSharePublicId(t) || formulaSharePublicId(t);
}

async function runToastSecondaryAction(t: NotificationItem) {
  if (formulaSharePublicId(t)) {
    await saveFormulaShareFromToast(t);
    return;
  }
  await copyShareArchiveFromToast(t);
}

// Repeated manual urges keep the notification ID but still represent a new reminder.
function toastSignatureKey(item: NotificationItem): string {
  return item.type === 'ISSUE_URGE' ? `${item.id}:${item.mergedCount ?? 1}` : String(item.id);
}

async function fetchToasts() {
  try {
    const list = await getPendingToasts(8);
    const reroutedIds = new Set<number>();
    for (const item of list) {
      const rerouted = await rerouteBellToastToTabDot(item, async (id) => {
        await markNotificationRead(id);
        reroutedIds.add(id);
      });
      if (rerouted) {
        emit('refresh-unread');
      }
    }
    const visible = list.filter((item) => !reroutedIds.has(item.id));
    const sig = visible.map(toastSignatureKey).join(',');
    if (!toastPollPrimed) {
      toastPollPrimed = true;
      lastToastSignature.value = sig;
      pendingToasts.value = visible;
      return;
    }
    if (sig !== lastToastSignature.value && visible.length > 0) {
      const prev = new Set(lastToastSignature.value.split(',').filter(Boolean));
      const hasNew = visible.some((x) => !prev.has(toastSignatureKey(x)));
      if (hasNew) {
        toastPopoverOpen.value = true;
        startAutoDismiss();
        bellPulse.value = true;
        window.setTimeout(() => {
          bellPulse.value = false;
        }, 4500);
      }
    }
    lastToastSignature.value = sig;
    pendingToasts.value = visible;
  } catch {
    /* ignore */
  }
}

function navigateAction(url?: string | null) {
  const navigation = resolveNotificationNavigation(url);
  if (navigation.kind === 'external') {
    window.location.assign(navigation.target);
    return;
  }
  router.push(navigation.target);
}

async function openToast(t: NotificationItem) {
  try {
    await markNotificationRead(t.id);
    emit('refresh-unread');
  } catch {
    /* ignore */
  }
  stopAutoDismiss();
  toastPopoverOpen.value = false;
  navigateAction(t.actionUrl);
}

async function copyShareArchiveFromToast(t: NotificationItem) {
  const pid = xindiSharePublicId(t);
  if (!pid) {
    ElMessage.warning('缺少分享标识');
    return;
  }
  try {
    const res = await copyXindiShareToArchive(pid);
    appendArchivedShareJobToLocalStorage({
      clientJobId: res.clientJobId,
      objectPrefix: res.objectPrefix,
      viewPath: res.viewPath,
      title: archiveTitleFromNotification(t),
    });
    ElMessage.success('已复制到您的个人归档目录');
    try {
      await markNotificationRead(t.id);
      emit('refresh-unread');
    } catch {
      /* ignore */
    }
    void fetchToasts();
  } catch (e) {
    const msg =
      typeof e === 'object' && e !== null && 'message' in e && typeof (e as { message?: string }).message === 'string'
        ? (e as { message: string }).message
        : '复制失败';
    ElMessage.error(msg);
  }
}

async function saveFormulaShareFromToast(t: NotificationItem) {
  const pid = formulaSharePublicId(t);
  if (!pid) {
    ElMessage.warning('缺少分享标识');
    return;
  }
  try {
    const res = await saveCustomFormulaShareToMine(pid);
    ElMessage.success(
      res.alreadySaved
        ? `公式「${res.formulaName}」已在您的列表中`
        : `公式「${res.formulaName}」已保存到您的列表`,
    );
    try {
      await markNotificationRead(t.id);
      emit('refresh-unread');
    } catch {
      /* ignore */
    }
    stopAutoDismiss();
    toastPopoverOpen.value = false;
    void fetchToasts();
    router.push({ name: 'AiCalculatorDetail', params: { formulaId: res.formulaId }, query: { isCustom: '1' } });
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  }
}

function goNotificationCenter() {
  stopAutoDismiss();
  toastPopoverOpen.value = false;
  router.push('/notifications');
}

function onBellClick() {
  if (toastPopoverOpen.value) {
    stopAutoDismiss();
    toastPopoverOpen.value = false;
    return;
  }
  void fetchToasts().then(() => {
    stopAutoDismiss();
    toastPopoverOpen.value = true;
  });
}

watch(
  () => props.unreadCount,
  (n) => {
    if (n === 0 && !pendingToasts.value.length) {
      stopAutoDismiss();
      toastPopoverOpen.value = false;
    }
  },
);

onMounted(() => {
  void fetchToasts();
  pollTimer = window.setInterval(() => void fetchToasts(), POLL_MS);
});

onUnmounted(() => {
  stopAutoDismiss();
  if (pollTimer != null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
});
</script>

<style scoped>
.bell-trigger {
  position: relative;
  min-width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--el-border-color-lighter);
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #4e5969;
}

.bell-inner {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}

.bell-icon {
  font-size: 18px;
}

.bell-badge-floating {
  position: absolute;
  top: -8px;
  right: -10px;
  pointer-events: none;
}

.bell-badge-floating :deep(.el-badge__content) {
  pointer-events: none;
}

.bell-trigger:hover {
  background: var(--el-fill-color-light);
}

.bell-pulse {
  animation: bell-pulse-anim 1.1s ease-in-out 2;
  box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.45);
}

@keyframes bell-pulse-anim {
  0% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.35);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(64, 158, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0);
  }
}

.bell-toast-panel {
  display: flex;
  flex-direction: column;
}

.bell-toast-empty {
  padding: 12px 4px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  text-align: center;
}

.bell-toast-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 168px;
  overflow: hidden;
}

.bell-toast-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.bell-toast-item:last-child {
  border-bottom: none;
}

.bell-toast-text {
  flex: 1;
  min-width: 0;
}

.bell-toast-title {
  font-size: 13px;
  color: var(--el-text-color-primary);
  line-height: 1.45;
}

.bell-toast-sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.bell-toast-link {
  flex-shrink: 0;
  padding: 0;
  font-size: 13px;
}

.bell-toast-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}

.bell-toast-link-secondary {
  font-weight: 500;
}

.bell-toast-footer {
  padding-top: 8px;
  margin-top: 4px;
  border-top: 1px solid var(--el-border-color-lighter);
  text-align: center;
}
</style>

<style>
.header-bell-toast-popper.el-popper {
  padding: 12px 14px 10px;
  z-index: 4600 !important;
}

.header-bell-toast-transition-enter-active,
.header-bell-toast-transition-leave-active {
  will-change: opacity;
}

.header-bell-toast-transition-enter-active {
  transition: opacity 0ms ease;
}

.header-bell-toast-transition-leave-active {
  transition: opacity 500ms ease;
}

.header-bell-toast-transition-enter-from,
.header-bell-toast-transition-leave-to {
  opacity: 0;
}

.header-bell-toast-popper.bell-toast-fading.el-popper {
  opacity: 0.15;
  pointer-events: auto;
}
</style>
